import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { WebContent } from '@euler/app/components/WebContent';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useTermsOfServiceHtmlSource } from '@euler/functions/tos/useTermsOfServiceHtmlSource';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { useBehaviorSubject } from '@euler/utils/hooks';
import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
} from '@gorhom/bottom-sheet';
import { memo, useCallback } from 'react';
import { View } from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

export const TermsOfServiceModal = memo(() => {
  const {
    navigation,
    showTermsOfServiceModal$,
    showDigitalSignModal$,
    isSignUnavailable$,
    step$,
    storeConfiguration,
  } = useOrderFlowContext();
  const { navBarHeight } = useSystemMetrics();
  const { rpx } = useResponsiveContext();
  const [visible] = useBehaviorSubject(showTermsOfServiceModal$);
  const [step] = useBehaviorSubject(step$);

  const source = useTermsOfServiceHtmlSource();

  const onDismiss = useCallback(() => {
    showTermsOfServiceModal$.next(false);
  }, [showTermsOfServiceModal$]);

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: visible,
      topMargin: navBarHeight + rpx(20),
      minSnapPoint: '45%',
      mediumSnapPoint: '75%',
      index: 2,
      enableDismissOnClose: true,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss,
    });

  const animatedPosition = useSharedValue(0);
  const height = useGeneralBottomSheetHeight();
  const style = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        animatedPosition.value,
        [(height * 2) / 4, height],
        [1, 0],
      ),
    };
  });

  const onContinueWithoutSign = useCallback(() => {
    showTermsOfServiceModal$.next(false);
    isSignUnavailable$.next(true);
    navigation.navigate('_previewOrder', {});
  }, [isSignUnavailable$, navigation, showTermsOfServiceModal$]);

  const onContinueSign = useCallback(() => {
    showTermsOfServiceModal$.next(false);
    showDigitalSignModal$.next(true);
  }, [showDigitalSignModal$, showTermsOfServiceModal$]);

  const onSkip = useCallback(() => {
    showTermsOfServiceModal$.next(false);
    if (step === 'order-info') {
      navigation.navigate('_previewOrder', {});
    }
  }, [navigation, showTermsOfServiceModal$, step]);

  const canSkip =
    !storeConfiguration.app?.orderFlow?.fields?.customerSign?.required;

  const Footer = useCallback(
    (props: BottomSheetFooterProps) => {
      return (
        <BottomSheetFooter {...props}>
          <BottomAction style={style}>
            {canSkip && (
              <BottomAction.Button
                text="客户不在店"
                variant="danger"
                onPress={onContinueWithoutSign}
              />
            )}
            <BottomAction.Button
              text="同意并签字"
              variant="primary"
              onPress={onContinueSign}
              css={`
                margin-left: ${rpx(15)}px;
              `}
            />
          </BottomAction>
        </BottomSheetFooter>
      );
    },
    [onContinueSign, onContinueWithoutSign, rpx, style, canSkip],
  );

  return (
    <BottomSheetModal
      {...bottomSheetProps}
      ref={bottomSheetModalRef}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="none"
      footerComponent={Footer}
      animatedPosition={animatedPosition}
    >
      {canSkip && (
        <HStack
          justifyContent="flex-end"
          css={`
            padding-right: ${rpx(15)}px;
            margin-top: ${rpx(0)}px;
            height: ${rpx(20)}px;
          `}
        >
          <LinkButton
            text="跳过"
            hitSlop={PredefinedHitSlops.large}
            textStyle={{ lineHeight: rpx(20), color: '#666' }}
            onPress={onSkip}
          />
          <Icon name="chevron-right" size={rpx(20)} color="#666" />
        </HStack>
      )}
      <View
        css={`
          flex: 1;
          padding-bottom: ${rpx(110)}px;
        `}
      >
        <WebContent
          showsVerticalScrollIndicator={false}
          css={`
            flex: 1;
          `}
          containerStyle={{
            paddingLeft: rpx(15),
            paddingRight: rpx(15),
          }}
          source={source}
        />
      </View>
    </BottomSheetModal>
  );
});
